import { useMemo, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { match } from 'ts-pattern'

import { fetchQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api/utils'
import {
  Button,
  Flex,
  Modal,
  useModalClose,
  useNotifications
} from '@sequencehq/core-components'
import { MINUTES } from '@sequencehq/utils/dates'

import { CreditNote } from 'CreditNotes/types'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { InvoiceListItem } from 'Invoices/types'
import { getHumanReadableInvoiceNumber } from 'Invoices/utils'

import { ApplyCreditsToSection } from './ApplyCreditsToSection'
import { ApplyCreditTotalsSection } from './ApplyCreditTotalsSection'
import {
  APPLY_CREDITS_ERROR_MESSAGE,
  APPLY_CREDITS_SUCCESS_MESSAGE,
  VERSION_CONFLICT_MESSAGE
} from './applyCreditNote.constants'
import {
  ApplyCreditRow,
  balanceValueToAmount,
  canSubmitApplyCredits,
  getApplyTotals,
  seedRowAmount
} from './applyCreditNote.helpers'
import { ApplyCreditsResult, useApplyCreditNote } from './useApplyCreditNote'

/** Drawer state tracks the credit note balance as a decimal string. */
const toCreditBalance = (balance: { value: number; version: number }) => ({
  value: balanceValueToAmount(balance.value),
  version: balance.version
})

/**
 * Applies a SENT credit note's remaining balance across the customer's
 * outstanding invoices. Mounted while open, so the rows reset on each open.
 */
export const ApplyCreditNoteDrawer = ({
  creditNoteId,
  onClose
}: {
  creditNoteId: string
  onClose: () => void
}) => (
  <Modal
    isOpen
    onClose={onClose}
    variant="drawer"
    // Matches the issue-credit-note drawer, so the two credit note flows read
    // at the same width.
    width="760px"
    data-testid="creditNote.applyCredits.drawer"
  >
    <Modal.Header showCloseButton withBorder>
      Apply credit note
    </Modal.Header>
    <DrawerBodyContent creditNoteId={creditNoteId} />
  </Modal>
)

const DrawerBodyContent = ({ creditNoteId }: { creditNoteId: string }) => {
  const creditNoteQuery = useQuery(
    dashboardv99990101Client.getCreditNote,
    { id: creditNoteId },
    { staleTime: 1 * MINUTES }
  )

  const creditNote = creditNoteQuery.data

  // The header button only shows for a credit note with a positive balance,
  // so a missing balance here means the data hasn't arrived yet.
  if (!creditNote?.balance) {
    return null
  }

  return <DrawerForm creditNote={creditNote} balance={creditNote.balance} />
}

const DrawerForm = ({
  creditNote,
  balance
}: {
  creditNote: CreditNote
  balance: string
}) => {
  const navigate = useNavigate()
  const closeDrawer = useModalClose()
  const { displayNotification } = useNotifications()
  const { applyCredits } = useApplyCreditNote({
    creditNoteId: creditNote.id,
    currency: creditNote.currency
  })

  const [rows, setRows] = useState<ApplyCreditRow[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Re-quoted from a conflict response so the next attempt uses live figures.
  const [creditBalance, setCreditBalance] = useState({
    value: balance,
    version: creditNote.balanceVersion ?? 0
  })

  const totals = useMemo(
    () => getApplyTotals(creditBalance.value, rows),
    [creditBalance.value, rows]
  )

  const handleSelectInvoice = async (invoice: InvoiceListItem) => {
    const { balance: invoiceBalance, balanceVersion: invoiceBalanceVersion } =
      invoice
    if (invoiceBalance === undefined || invoiceBalanceVersion === undefined) {
      displayNotification('Failed to load invoice balance', {
        type: 'error',
        'data-testid': 'creditNote.applyCredits.balanceLoadError'
      })
      return
    }

    const rowId = crypto.randomUUID()

    setRows(previous => [
      ...previous,
      {
        rowId,
        invoiceId: invoice.id,
        invoiceNumber: getHumanReadableInvoiceNumber({
          invoiceNumber: invoice.invoiceNumber,
          invoiceStatus: invoice.status
        }),
        dueDate: invoice.dueDate,
        invoiceBalance,
        invoiceBalanceVersion,
        amount: seedRowAmount(totals.creditRemaining, invoiceBalance),
        eligibility: 'pending'
      }
    ])

    // Account-level reasons only surface per invoice, so eligibility is
    // resolved for the invoice just picked and patched onto its row.
    try {
      const eligibility = await fetchQuery(
        apiQueryClient,
        dashboardv99990101Client.getCreditNoteApplicationEligibility,
        { invoiceId: invoice.id },
        { staleTime: 1 * MINUTES }
      )

      setRows(previous =>
        previous.map(row =>
          row.rowId === rowId
            ? {
                ...row,
                eligibility: eligibility?.eligible
                  ? 'eligible'
                  : { ineligible: eligibility?.reasons ?? [] }
              }
            : row
        )
      )
    } catch {
      // A row stuck 'pending' blocks submit silently, so drop it instead.
      setRows(previous => previous.filter(row => row.rowId !== rowId))
      displayNotification('Failed to check invoice eligibility', {
        type: 'error',
        'data-testid': 'creditNote.applyCredits.eligibilityLoadError'
      })
    }
  }

  const handleFailure = (result: ApplyCreditsResult) => {
    const failure = result.failed
    if (!failure) return

    const nextBalance = match(failure)
      .with({ kind: 'conflict' }, ({ conflict }) =>
        toCreditBalance(conflict.currentCreditNoteBalance)
      )
      .with({ kind: 'error' }, () =>
        result.creditNoteBalance
          ? toCreditBalance(result.creditNoteBalance)
          : creditBalance
      )
      .exhaustive()

    setCreditBalance(nextBalance)

    const succeededRowIds = new Set(result.succeeded.map(row => row.rowId))

    setRows(previous =>
      previous.reduce<ApplyCreditRow[]>((acc, row) => {
        if (succeededRowIds.has(row.rowId)) return acc

        if (failure.kind === 'conflict' && failure.rowId === row.rowId) {
          const conflicted = failure.conflict.currentInvoiceBalance
          const invoiceBalance = balanceValueToAmount(conflicted.value)

          return [
            ...acc,
            {
              ...row,
              invoiceBalance,
              invoiceBalanceVersion: conflicted.version,
              amount: seedRowAmount(nextBalance.value, invoiceBalance)
            }
          ]
        }

        return [...acc, row]
      }, [])
    )

    displayNotification(APPLY_CREDITS_ERROR_MESSAGE, {
      type: 'error',
      description: match(failure)
        .with({ kind: 'conflict' }, () => VERSION_CONFLICT_MESSAGE)
        .with({ kind: 'error' }, ({ message }) => message || undefined)
        .exhaustive()
    })
  }

  const handleSuccess = (result: ApplyCreditsResult) => {
    const singleInvoiceId =
      result.succeeded.length === 1
        ? result.succeeded.at(0)?.invoiceId
        : undefined

    displayNotification(APPLY_CREDITS_SUCCESS_MESSAGE, {
      type: 'success',
      ...(singleInvoiceId
        ? {
            viewLabel: 'View invoice',
            onClickView: () => navigate(`/invoices/${singleInvoiceId}`)
          }
        : {})
    })

    closeDrawer?.()
  }

  const handleApply = async () => {
    setIsSubmitting(true)
    const result = await applyCredits({
      rows,
      creditNoteBalanceVersion: creditBalance.version
    })
    setIsSubmitting(false)

    if (result.failed) {
      handleFailure(result)
      return
    }

    handleSuccess(result)
  }

  const canSubmit = canSubmitApplyCredits({
    rows,
    creditNoteBalance: creditBalance.value,
    totals
  })

  return (
    <>
      <Modal.Content>
        <Flex flexDir="column" gap="20px">
          <ApplyCreditsToSection
            rows={rows}
            customerId={creditNote.customerId}
            currency={creditNote.currency}
            creditNoteBalance={creditBalance.value}
            totals={totals}
            onChangeAmount={(rowId, amount) =>
              setRows(previous =>
                previous.map(row =>
                  row.rowId === rowId ? { ...row, amount } : row
                )
              )
            }
            onRemoveRow={rowId =>
              setRows(previous => previous.filter(row => row.rowId !== rowId))
            }
            onSelectInvoice={handleSelectInvoice}
          />
          {/* Shown from the start, so the credit note's totals and remaining
              balance are visible before any invoice is picked. */}
          <ApplyCreditTotalsSection creditNote={creditNote} totals={totals} />
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Modal.CancelButton data-testid="creditNote.applyCredits.cancel" />
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={!canSubmit}
          submitting={isSubmitting}
          submittingText="Applying..."
          data-testid="creditNote.applyCredits.submit"
        >
          Apply
        </Button>
      </Modal.Footer>
    </>
  )
}
